import React, { useState, useRef, useEffect } from 'react';
import { X, Search, Loader2, Package, AlertTriangle } from 'lucide-react';
import { formatUnitPrice, formatCurrency } from '../../utils/formatting';
import { apiService } from '../../services/apiService';
import ItemSearchModal from './ItemSearchModal';

export default function AddLineItemModal({
  selectedItems,
  customerNumber,
  shipToNumber,
  correlationId,
  user,
  // Threaded through to ItemSearchModal. Without it the search silently falls back to
  // "All warehouses" — the CSR sees items they cannot order from this order's warehouse,
  // "across N WHs" labels, and no warehouse/all radio at all (Tonya, 2026-08-13).
  warehouseId,
  onAdd,
  onClose,
}) {
  // Compute default line number from existing items
  const defaultLineNumber = selectedItems && selectedItems.length > 0
    ? Math.max(...selectedItems.map(i => i.poLineNumber || 0)) + 1
    : 1;

  const [lineNumber, setLineNumber] = useState(defaultLineNumber);

  // True when the entered number already belongs to a line on this order. Inserting no longer
  // renumbers the existing lines, so the CSR should know they're creating a duplicate ref.
  const duplicatesExistingLine = (selectedItems || []).some(
    i => i.poLineNumber != null && Number(i.poLineNumber) === parseInt(lineNumber, 10)
  );
  const [selectedItem, setSelectedItem] = useState(null); // { item_number, description, unit_of_measure, val_price_unit_of_measure, val_price_unit_conversion, inventory }
  const [showItemSearch, setShowItemSearch] = useState(false);

  const [quantity, setQuantity] = useState('');
  const [qtyInputValue, setQtyInputValue] = useState('');

  // The customer's A+ default U/M for the selected item (CDFUM, via /api/itemUoms), so a line
  // can be keyed as the customer orders it: 2 SK rather than 320 EA (Tonya, 2026-11-01, 6210594).
  // { code, factor } with factor = customer units per stock unit; null when there's none.
  const [customerUnit, setCustomerUnit] = useState(null);
  const [qtyInCustomerUnit, setQtyInCustomerUnit] = useState(false);

  const [priceLoading, setPriceLoading] = useState(false);
  const [priceResult, setPriceResult] = useState(null); // raw API response
  const [priceError, setPriceError] = useState(null);

  // Derived pricing fields — separate editable state for contract price and stock UOM price
  const [contractPriceInput, setContractPriceInput] = useState('');
  const [stockUomPriceInput, setStockUomPriceInput] = useState('');

  // For no-contract scenario: single unit price field
  const [unitPriceInput, setUnitPriceInput] = useState('');

  const lineNumberRef = useRef(null);

  useEffect(() => {
    lineNumberRef.current?.focus();
  }, []);

  // --- Helpers ---

  const hasContractPricing = priceResult && priceResult.val_unit_price != null;
  const conversionFactor = priceResult?.val_price_unit_conversion || selectedItem?.val_price_unit_conversion || null;
  const priceUom = priceResult?.val_price_unit_of_measure || selectedItem?.val_price_unit_of_measure || null;
  const stockUom = selectedItem?.unit_of_measure || null;
  const hasConversion = hasContractPricing && conversionFactor && conversionFactor !== 1 && priceUom && stockUom && priceUom !== stockUom;

  // Initialize price inputs when priceResult arrives
  useEffect(() => {
    if (!priceResult) return;

    if (hasConversion) {
      // Scenario A: contract price per priceUOM, stock price derived
      const contractPrice = priceResult.val_unit_price;
      const stockPrice = contractPrice * conversionFactor;
      setContractPriceInput(contractPrice != null ? String(Number(contractPrice).toFixed(5)) : '');
      setStockUomPriceInput(stockPrice != null ? String(Number(stockPrice).toFixed(5)) : '');
    } else if (hasContractPricing) {
      // Scenario B: single contract price
      setContractPriceInput(priceResult.val_unit_price != null ? String(Number(priceResult.val_unit_price).toFixed(5)) : '');
    } else {
      // Scenario C: no contract — empty unit price
      setUnitPriceInput('');
    }
  }, [priceResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Item selection ---

  const handleItemSelect = (itemIndex, item) => {
    setSelectedItem(item);
    setShowItemSearch(false);
    setCustomerUnit(null);
    setQtyInCustomerUnit(false);
    loadCustomerUnit(item);
    // Reset pricing when item changes
    setPriceResult(null);
    setPriceError(null);
    setContractPriceInput('');
    setStockUomPriceInput('');
    setUnitPriceInput('');
    // If quantity already entered, trigger price validation
    const parsedQty = parseFloat(quantity);
    if (!isNaN(parsedQty) && parsedQty > 0) {
      triggerPriceValidation(item, parsedQty);
    }
  };

  const loadCustomerUnit = async (item) => {
    if (!item?.item_number || !customerNumber) return;
    try {
      const data = await apiService.getItemUoms(item.item_number, customerNumber);
      const code = (data.customer_default_uom || '').trim().toUpperCase();
      const stock = (item.unit_of_measure || '').trim().toUpperCase();
      const unit = (data.uoms || []).find(u => (u.code || '').trim().toUpperCase() === code);
      if (code && code !== stock && unit?.conversion_factor > 0) {
        setCustomerUnit({ code, factor: unit.conversion_factor });
        setQtyInCustomerUnit(true);
      }
    } catch {
      // No customer unit offered; the line is keyed in the stock unit as before.
    }
  };

  // Stock-unit quantity for what was keyed: 2 SK at 1/160 SK per EA -> 320 EA.
  const toStockQty = (entered, inCustomerUnit = qtyInCustomerUnit) =>
    inCustomerUnit && customerUnit ? Math.round((entered / customerUnit.factor) * 100000) / 100000 : entered;

  // --- Quantity ---

  const handleQtyChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    setQtyInputValue(raw);
  };

  const handleQtyCommit = () => {
    const parsed = parseFloat(qtyInputValue);
    if (isNaN(parsed) || parsed <= 0) return;
    if (!selectedItem) return;
    setQuantity(parsed);
    triggerPriceValidation(selectedItem, toStockQty(parsed));
  };

  const handleQtyUnitChange = (inCustomerUnit) => {
    setQtyInCustomerUnit(inCustomerUnit);
    const entered = parseFloat(quantity);
    if (selectedItem && entered > 0) triggerPriceValidation(selectedItem, toStockQty(entered, inCustomerUnit));
  };

  const handleQtyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      handleQtyCommit();
    }
  };

  // --- Price validation ---

  const triggerPriceValidation = async (item, qty) => {
    setPriceLoading(true);
    setPriceError(null);
    setPriceResult(null);
    setContractPriceInput('');
    setStockUomPriceInput('');
    setUnitPriceInput('');

    try {
      const result = await apiService.validatePrice(
        item.item_number,
        customerNumber,
        qty,
        null,  // poUnitPrice — no PO data for added lines
        null,  // poUnitOfMeasure — no PO data for added lines
        item.unit_of_measure,
        correlationId,
        user?.userId,
        user?.userEmail,
        warehouseId
      );
      setPriceResult(result);
    } catch (err) {
      console.error('Price validation failed:', err);
      setPriceError(err.message || 'Price validation failed');
    } finally {
      setPriceLoading(false);
    }
  };

  // --- Linked price field editing ---

  const handleContractPriceChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    setContractPriceInput(raw);
    // Recompute stock UOM price
    if (hasConversion && raw !== '') {
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) {
        setStockUomPriceInput(Number(parsed * conversionFactor).toFixed(5));
      }
    }
  };

  const handleStockUomPriceChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    setStockUomPriceInput(raw);
    // Back-compute contract price
    if (hasConversion && raw !== '' && conversionFactor !== 0) {
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) {
        setContractPriceInput(Number(parsed / conversionFactor).toFixed(5));
      }
    }
  };

  // --- Compute final stock UOM price for onAdd ---

  const getFinalStockUomPrice = () => {
    if (hasConversion) {
      const parsed = parseFloat(stockUomPriceInput);
      return isNaN(parsed) ? null : parsed;
    } else if (hasContractPricing) {
      const parsed = parseFloat(contractPriceInput);
      return isNaN(parsed) ? null : parsed;
    } else {
      const parsed = parseFloat(unitPriceInput);
      return isNaN(parsed) ? null : parsed;
    }
  };

  // --- Add button validation ---

  // parsedQty is always in the stock unit (what the line goes to A+ in).
  const enteredQty = parseFloat(quantity);
  const parsedQty = isNaN(enteredQty) ? NaN : toStockQty(enteredQty);
  const fractionalStockQty = qtyInCustomerUnit && customerUnit && parsedQty > 0
    && Math.abs(parsedQty - Math.round(parsedQty)) > 1e-6 && (stockUom || '').toUpperCase() !== 'M';
  const validQty = !isNaN(parsedQty) && parsedQty > 0 && !fractionalStockQty;
  const finalPrice = getFinalStockUomPrice();
  const isAddDisabled = !selectedItem || !validQty || finalPrice == null || priceLoading;

  // --- Handle Add ---

  const handleAdd = () => {
    if (isAddDisabled) return;
    onAdd({
      lineNumber: parseInt(lineNumber, 10) || defaultLineNumber,
      item: selectedItem,
      quantity: parsedQty,
      stockUom: stockUom,
      customerUnit: qtyInCustomerUnit && customerUnit
        ? { uom: customerUnit.code, quantity: enteredQty } : null,
      customerDefaultUom: customerUnit?.code || null,
      finalStockUomPrice: getFinalStockUomPrice(),
      priceResult,
    });
  };

  return (
    <>
      {/* Main modal */}
      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center animate-fadeIn"
      >
        <div
          className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 animate-scaleIn flex flex-col max-h-[90vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Add Line Item</h3>
                <p className="text-xs text-slate-400">Search A+ catalog and set quantity &amp; price</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-5">

            {/* Step 1: Line Number */}
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                PO Line Number
              </label>
              <input
                ref={lineNumberRef}
                type="number"
                min="1"
                value={lineNumber}
                onChange={e => setLineNumber(e.target.value)}
                className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-colors"
              />
              {/* Adding a line no longer renumbers the other lines (their numbers are the
                  customer's own and go out on EDI invoices), so a duplicate is possible —
                  warn rather than silently shifting everything below. */}
              {duplicatesExistingLine && (
                <div className="mt-1.5 text-[11px] text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                  Line {parseInt(lineNumber, 10)} is already on this order — both lines will send
                  the same PO line number to A+.
                </div>
              )}
            </div>

            {/* Step 2: Item Selection */}
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                Item
              </label>

              {!selectedItem ? (
                <button
                  onClick={() => setShowItemSearch(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-slate-600 hover:border-amber-500/50 bg-slate-900/50 hover:bg-slate-900 text-sm text-slate-400 hover:text-amber-400 transition-all w-full justify-center"
                >
                  <Search className="w-4 h-4" />
                  Search A+ Catalog
                </button>
              ) : (
                <div className="bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-white text-sm font-semibold">{selectedItem.item_number}</span>
                        {selectedItem.unit_of_measure && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400 font-medium uppercase">
                            {selectedItem.unit_of_measure}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-300 mt-0.5 leading-snug">{selectedItem.description}</p>
                      {selectedItem.inventory && (
                        <p className={`text-xs mt-1 ${
                          selectedItem.inventory.quantity_on_hand > 0 ? 'text-emerald-500' : 'text-red-400'
                        }`}>
                          {selectedItem.inventory.quantity_on_hand > 0
                            ? `${Number(selectedItem.inventory.quantity_on_hand).toLocaleString()} ${selectedItem.unit_of_measure || ''} in stock`.trim()
                            : 'Out of stock'}
                          {selectedItem.inventory.warehouse_id && (
                            <span className="text-slate-500 ml-1">(WH: {selectedItem.inventory.warehouse_id})</span>
                          )}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setShowItemSearch(true)}
                      className="flex-shrink-0 text-xs text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1 mt-0.5"
                    >
                      <Search className="w-3 h-3" />
                      Change
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Quantity (only after item selected) */}
            {selectedItem && (
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                  Quantity
                  {stockUom && !customerUnit && <span className="normal-case ml-1 text-slate-600">({stockUom})</span>}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={qtyInputValue}
                    onChange={handleQtyChange}
                    onBlur={handleQtyCommit}
                    onKeyDown={handleQtyKeyDown}
                    placeholder="Enter quantity..."
                    className="w-36 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-colors"
                  />
                  {customerUnit && (
                    <div className="flex rounded-lg border border-slate-600 overflow-hidden text-xs" title="Customer Default UM is set up in A+ for this customer and item">
                      {[[true, customerUnit.code], [false, stockUom]].map(([inCust, code]) => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => handleQtyUnitChange(inCust)}
                          className={`px-2.5 py-1.5 font-mono transition-colors ${qtyInCustomerUnit === inCust
                            ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  )}
                  {priceLoading && (
                    <Loader2 className="w-4 h-4 text-amber-400 animate-spin flex-shrink-0" />
                  )}
                </div>
                {customerUnit && qtyInCustomerUnit && enteredQty > 0 && (
                  <div className={`mt-1.5 text-[11px] ${fractionalStockQty ? 'text-amber-400' : 'text-slate-500'}`}>
                    Customer Default UM: {enteredQty} {customerUnit.code} = {parsedQty.toLocaleString()} {stockUom}
                    {fractionalStockQty && ' — not a whole quantity; enter it in ' + stockUom + ' instead'}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Pricing (after price result available) */}
            {selectedItem && validQty && !priceLoading && (priceResult || priceError) && (
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                  Pricing
                </label>

                {priceError && (
                  <div className="flex items-center gap-2 bg-red-900/20 border border-red-700/40 rounded-lg px-4 py-3">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-400">{priceError}</p>
                  </div>
                )}

                {priceResult && (
                  <div className="bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 space-y-3">

                    {/* Scenario A: Contract pricing + conversion */}
                    {hasConversion && (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400">Contract</span>
                          <span className="text-xs text-slate-400">
                            Conversion: {conversionFactor} {priceUom} / {stockUom}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                              Contract Price (per {priceUom})
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={contractPriceInput}
                                onChange={handleContractPriceChange}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-white font-mono text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-colors"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                              Unit Price (per {stockUom})
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={stockUomPriceInput}
                                onChange={handleStockUomPriceChange}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-white font-mono text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-colors"
                              />
                            </div>
                          </div>
                        </div>
                        {stockUomPriceInput && parsedQty > 0 && (
                          <div className="text-xs text-slate-400 text-right pt-1">
                            {parsedQty} {stockUom} @ {formatUnitPrice(parseFloat(stockUomPriceInput))} = <span className="text-white font-medium">{formatCurrency(parsedQty * parseFloat(stockUomPriceInput))}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Scenario B: Contract pricing, no conversion */}
                    {hasContractPricing && !hasConversion && (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400">Contract</span>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                            Contract Price {stockUom ? `(per ${stockUom})` : ''}
                          </label>
                          <div className="relative w-48">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={contractPriceInput}
                              onChange={e => setContractPriceInput(e.target.value.replace(/[^0-9.]/g, ''))}
                              className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-white font-mono text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/30 transition-colors"
                            />
                          </div>
                        </div>
                        {contractPriceInput && parsedQty > 0 && (
                          <div className="text-xs text-slate-400 text-right pt-1">
                            {parsedQty} {stockUom} @ {formatUnitPrice(parseFloat(contractPriceInput))} = <span className="text-white font-medium">{formatCurrency(parsedQty * parseFloat(contractPriceInput))}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Scenario C: No contract pricing */}
                    {!hasContractPricing && (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                          <span className="text-xs text-amber-400">No contract pricing found — enter unit price manually</span>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                            Unit Price {stockUom ? `(per ${stockUom})` : ''}
                          </label>
                          <div className="relative w-48">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={unitPriceInput}
                              onChange={e => setUnitPriceInput(e.target.value.replace(/[^0-9.]/g, ''))}
                              className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-white font-mono text-sm focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/30 transition-colors"
                            />
                          </div>
                        </div>
                        {unitPriceInput && parsedQty > 0 && (
                          <div className="text-xs text-slate-400 text-right pt-1">
                            {parsedQty} {stockUom} @ {formatUnitPrice(parseFloat(unitPriceInput))} = <span className="text-white font-medium">{formatCurrency(parsedQty * parseFloat(unitPriceInput))}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-slate-700 flex items-center justify-between flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 transition-all text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={isAddDisabled}
              className={`px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
                isAddDisabled
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20'
              }`}
            >
              {priceLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Package className="w-4 h-4" />
                  Add Line Item
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Item Search sub-modal — rendered on top */}
      {showItemSearch && (
        <ItemSearchModal
          item={null}
          itemIndex={-1}
          customerNumber={customerNumber}
          shiptoNumber={shipToNumber}
          warehouseId={warehouseId}
          correlationId={correlationId}
          user={user}
          onSelect={handleItemSelect}
          onClose={() => setShowItemSearch(false)}
        />
      )}
    </>
  );
}
